// KK (Kalite Kontrol) Prompt Library — spec §7. /admin/settings/prompt-library (Sistem Ayarları alt öğesi).
// qc_prompts.manage yetkisiyle görünür. AdminBranding iskeleti: sol rail (liste +
// arama + "Yeni Prompt") + sağ panel (form). Sol dayalı layout, maxWidth 1400.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  QC_PLACEHOLDER_TOKENS,
  QC_PROMPT_TYPES,
  QcPromptCreateSchema,
  QcPromptUpdateSchema,
  type QcPromptCreate,
  type QcPromptDto,
  type QcPromptType,
} from "@mahir/shared";
import {
  adminCreateQcPrompt,
  adminDeleteQcPrompt,
  adminListQcPrompts,
  adminUpdateQcPrompt,
} from "../../api/qc";

type Selection = { kind: "new" } | { kind: "edit"; id: string };

export function AdminPromptLibrary(): JSX.Element {
  const [prompts, setPrompts] = useState<QcPromptDto[]>([]);
  const [search, setSearch] = useState("");
  const [selection, setSelection] = useState<Selection>({ kind: "new" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (selectId?: string): Promise<void> => {
    setLoading(true);
    try {
      const res = await adminListQcPrompts();
      setPrompts(res.prompts);
      setError(null);
      if (selectId) setSelection({ kind: "edit", id: selectId });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = search.trim()
    ? prompts.filter((p) => p.name.toLowerCase().includes(search.trim().toLowerCase()))
    : prompts;

  const selected =
    selection.kind === "edit" ? (prompts.find((p) => p.id === selection.id) ?? null) : null;

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)", maxWidth: 1400 }}>
      <header className="mb-6">
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.72rem",
            letterSpacing: "0.08em",
            color: "var(--color-text-3)",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          ADMİN
        </div>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.8rem",
            color: "var(--color-text)",
          }}
        >
          KK Prompt Library
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--color-text-2)" }}>
          Kalite Kontrol promptları: Rewrite (yeniden yazım) + Critique (eleştiri). Aktif promptlar
          danışman KK dropdown'ında görünür. Placeholder'lar üretim anında çözümlenir.
        </p>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          gap: 24,
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          borderRadius: 12,
          overflow: "hidden",
          minHeight: 600,
        }}
      >
        {/* Sol rail — liste + arama + yeni */}
        <nav
          style={{
            padding: 12,
            borderRight: "1px solid var(--color-border)",
            background: "var(--color-bg)",
            overflowY: "auto",
            maxHeight: "calc(100vh - 240px)",
          }}
        >
          <button
            onClick={() => setSelection({ kind: "new" })}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "10px 12px",
              borderRadius: 6,
              marginBottom: 10,
              background: selection.kind === "new" ? "var(--color-surface)" : "transparent",
              border: "1px solid",
              borderColor:
                selection.kind === "new" ? "var(--color-primary)" : "var(--color-border)",
              color: selection.kind === "new" ? "var(--color-primary)" : "var(--color-text)",
              fontSize: "0.9rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            + Yeni Prompt
          </button>

          <input
            type="text"
            placeholder="Prompt ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 10px",
              border: "1px solid var(--color-border)",
              borderRadius: 6,
              fontSize: "0.85rem",
              marginBottom: 8,
            }}
          />

          {loading && (
            <div className="text-xs px-3" style={{ color: "var(--color-text-3)" }}>
              Yükleniyor…
            </div>
          )}
          {error && (
            <div className="text-xs px-3" style={{ color: "var(--color-error)" }}>
              {error}
            </div>
          )}
          {filtered.map((p) => {
            const isActive = selection.kind === "edit" && selection.id === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setSelection({ kind: "edit", id: p.id })}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 12px",
                  borderRadius: 6,
                  marginBottom: 2,
                  background: isActive ? "var(--color-surface)" : "transparent",
                  border: "1px solid",
                  borderColor: isActive ? "var(--color-primary)" : "transparent",
                  color: isActive ? "var(--color-primary)" : "var(--color-text-2)",
                  fontSize: "0.88rem",
                  fontWeight: isActive ? 600 : 400,
                  cursor: "pointer",
                  // Pasif kayıtlar soluk — aktif/pasif ayrımı bir bakışta.
                  opacity: p.isActive ? 1 : 0.5,
                }}
              >
                <span style={{ marginRight: 6 }}>{p.promptType === "rewrite" ? "✏️" : "🔍"}</span>
                {p.name}
              </button>
            );
          })}
          {!loading && filtered.length === 0 && (
            <div
              className="text-xs px-3 mt-2"
              style={{ color: "var(--color-text-3)", fontStyle: "italic" }}
            >
              Prompt bulunamadı
            </div>
          )}
        </nav>

        {/* Sağ panel — form */}
        <main style={{ padding: 24, minWidth: 0 }}>
          <PromptForm
            key={selection.kind === "edit" ? selection.id : "new"}
            prompt={selected}
            onSaved={(id) => void reload(id)}
            onDeleted={() => {
              setSelection({ kind: "new" });
              void reload();
            }}
          />
        </main>
      </div>
    </div>
  );
}

interface PromptFormProps {
  prompt: QcPromptDto | null;
  onSaved: (id: string) => void;
  onDeleted: () => void;
}

function PromptForm({ prompt, onSaved, onDeleted }: PromptFormProps): JSX.Element {
  const isEdit = prompt !== null;
  const [name, setName] = useState(prompt?.name ?? "");
  const [description, setDescription] = useState(prompt?.description ?? "");
  const [promptType, setPromptType] = useState<QcPromptType>(prompt?.promptType ?? "rewrite");
  const [promptText, setPromptText] = useState(prompt?.promptText ?? "");
  const [isActive, setIsActive] = useState(prompt?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState(prompt?.sortOrder ?? 0);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const textRef = useRef<HTMLTextAreaElement | null>(null);

  // Bilinmeyen token uyarısı (spec §7.4) — kataloğda olmayan {{token}}'lar
  // çözümlenmeden ham gider.
  const unknownTokens = useMemo(() => {
    const found = [...promptText.matchAll(/\{\{\s*([\w]+)\s*\}\}/g)].map((m) => m[1]!);
    return [...new Set(found)].filter((t) => !QC_PLACEHOLDER_TOKENS.includes(t as never));
  }, [promptText]);

  const typeChanged = isEdit && prompt.promptType !== promptType;

  const insertToken = (token: string): void => {
    const el = textRef.current;
    const snippet = `{{${token}}}`;
    if (!el) {
      setPromptText((t) => t + snippet);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = promptText.slice(0, start) + snippet + promptText.slice(end);
    setPromptText(next);
    // Cursor'ı eklenen token'ın sonuna taşı.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setFormError(null);
    const payload: QcPromptCreate = {
      name: name.trim(),
      description: description.trim() ? description.trim() : null,
      promptType,
      promptText,
      isActive,
      sortOrder,
    };
    // Shared Zod ile FE-tarafı doğrulama — edit'te partial, create'te tam şema.
    const parsed = isEdit
      ? QcPromptUpdateSchema.safeParse(payload)
      : QcPromptCreateSchema.safeParse(payload);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? "Geçersiz form");
      setSaving(false);
      return;
    }
    try {
      if (isEdit) {
        const res = await adminUpdateQcPrompt(prompt.id, parsed.data);
        onSaved(res.prompt.id);
      } else {
        const res = await adminCreateQcPrompt(parsed.data as QcPromptCreate);
        onSaved(res.prompt.id);
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!isEdit) return;
    setDeleting(true);
    setFormError(null);
    try {
      await adminDeleteQcPrompt(prompt.id);
      onDeleted();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Silinemedi");
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "var(--color-text)",
    marginBottom: 6,
  };
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    fontSize: "0.9rem",
    fontFamily: "var(--font-sans)",
    background: "var(--color-surface)",
    color: "var(--color-text)",
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", marginBottom: 8 }}>
        {isEdit ? prompt.name : "Yeni Prompt"}
      </h2>
      <p className="mb-4 text-sm" style={{ color: "var(--color-text-2)" }}>
        {isEdit
          ? "KK promptunu düzenle. Değişiklikler yeni KK çağrılarında geçerlidir."
          : "Yeni bir Kalite Kontrol promptu tanımla."}
      </p>

      <div className="flex flex-col gap-5">
        {/* İsim */}
        <div>
          <label style={labelStyle}>İsim</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Örn. GRI Rewrite — resmi ton"
            style={inputStyle}
          />
        </div>

        {/* Açıklama */}
        <div>
          <label style={labelStyle}>Açıklama</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Bu promptun ne yaptığına dair kısa not (opsiyonel)"
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>

        {/* Tip — radio */}
        <div>
          <label style={labelStyle}>Tip</label>
          <div style={{ display: "flex", gap: 12 }}>
            {QC_PROMPT_TYPES.map((t) => {
              const active = promptType === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setPromptType(t)}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid",
                    borderColor: active ? "var(--color-primary)" : "var(--color-border)",
                    background: active
                      ? "color-mix(in srgb, var(--color-primary) 10%, white)"
                      : "var(--color-surface)",
                    color: active ? "var(--color-primary)" : "var(--color-text-2)",
                    fontSize: "0.88rem",
                    fontWeight: active ? 600 : 400,
                    cursor: "pointer",
                  }}
                >
                  {t === "rewrite" ? "✏️ Rewrite" : "🔍 Critique"}
                </button>
              );
            })}
          </div>
          {typeChanged && (
            <div
              className="mt-2 px-3 py-2 rounded text-xs"
              style={{
                background: "color-mix(in srgb, var(--color-warning) 10%, white)",
                color: "var(--color-warning)",
                border: "1px solid color-mix(in srgb, var(--color-warning) 25%, white)",
              }}
            >
              ⚠ Tip değişimi çıktı sözleşmesini değiştirir — mevcut kullanıcı beklentilerini gözden
              geçirin.
            </div>
          )}
        </div>

        {/* Prompt metni + placeholder chip'leri */}
        <div>
          <label style={labelStyle}>Prompt Metni</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {QC_PLACEHOLDER_TOKENS.map((tok) => (
              <button
                key={tok}
                type="button"
                onClick={() => insertToken(tok)}
                title={`{{${tok}}} ekle`}
                style={{
                  padding: "3px 8px",
                  borderRadius: 999,
                  border: "1px solid var(--color-border)",
                  background: "var(--color-bg)",
                  color: "var(--color-text-2)",
                  fontSize: "0.72rem",
                  fontFamily: "var(--font-mono)",
                  cursor: "pointer",
                }}
              >
                {`{{${tok}}}`}
              </button>
            ))}
          </div>
          <textarea
            ref={textRef}
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            placeholder="KK talimatını yaz. Placeholder'lar üretim anında çözümlenir."
            style={{
              ...inputStyle,
              minHeight: 240,
              resize: "vertical",
              fontFamily: "var(--font-mono)",
              fontSize: "0.82rem",
              lineHeight: 1.6,
            }}
          />
          {unknownTokens.length > 0 && (
            <div
              className="mt-2 px-3 py-2 rounded text-xs"
              style={{
                background: "color-mix(in srgb, var(--color-warning) 10%, white)",
                color: "var(--color-warning)",
                border: "1px solid color-mix(in srgb, var(--color-warning) 25%, white)",
              }}
            >
              {unknownTokens.map((t) => (
                <div key={t}>⚠ {`{{${t}}}`} kataloğda yok — çözümlenmeden ham gider.</div>
              ))}
            </div>
          )}
        </div>

        {/* isActive + sortOrder */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 24 }}>
          <div>
            <label style={labelStyle}>Aktif</label>
            <SwitchToggle on={isActive} onChange={setIsActive} />
          </div>
          <div>
            <label style={labelStyle}>Sıra</label>
            <input
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              style={{ ...inputStyle, width: 120 }}
            />
          </div>
        </div>

        {formError && (
          <div
            className="px-3 py-2 rounded text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {formError}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className="disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              padding: "9px 20px",
              borderRadius: 8,
              border: "none",
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontSize: "0.9rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {saving ? "Kaydediliyor…" : isEdit ? "Kaydet" : "Oluştur"}
          </button>
          {isEdit && (
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
              style={{
                padding: "9px 20px",
                borderRadius: 8,
                border: "1px solid var(--color-border)",
                background: "transparent",
                color: "var(--color-error)",
                fontSize: "0.9rem",
                cursor: "pointer",
              }}
            >
              Sil
            </button>
          )}
        </div>
      </div>

      {/* Silme onay modalı */}
      {confirmDelete && isEdit && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ background: "rgba(0,0,0,0.4)" }}
          onClick={() => !deleting && setConfirmDelete(false)}
        >
          <div
            className="rounded-xl p-6 max-w-md w-full mx-4"
            style={{ background: "var(--color-surface)", color: "var(--color-text)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-2" style={{ fontFamily: "var(--font-display)", fontSize: "1.25rem" }}>
              Promptu Sil
            </h2>
            <p className="mb-5 text-sm" style={{ color: "var(--color-text-2)" }}>
              “{prompt.name}” kalıcı silinecek — geçmiş KK kayıtlarındaki iz korunur.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm"
                style={{
                  background: "transparent",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-text-2)",
                }}
              >
                Vazgeç
              </button>
              <button
                onClick={() => void handleDelete()}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: "var(--color-error)",
                  color: "var(--color-surface)",
                  fontWeight: 600,
                }}
              >
                {deleting ? "Siliniyor…" : "Kalıcı Sil"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SwitchToggle({
  on,
  disabled,
  onChange,
}: {
  on: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      style={{
        width: 36,
        height: 20,
        padding: 2,
        borderRadius: 999,
        border: "none",
        background: on ? "var(--color-primary)" : "var(--color-border)",
        position: "relative",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background 120ms ease",
        opacity: disabled ? 0.55 : 1,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          display: "block",
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: "var(--color-surface)",
          transform: on ? "translateX(16px)" : "translateX(0)",
          transition: "transform 120ms ease",
          boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
        }}
      />
    </button>
  );
}
